import React, { useState, useEffect } from 'react';
import {
  getRegularEmployeesMaster,
  saveRegularEmployeeMaster,
  deleteRegularEmployeeMaster,
  BUSINESS_ENTITIES
} from '../firebase/firestore';

const RegularEmployeeManagement = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    name: '',
    position: '',
    department: '',
    businessEntity: BUSINESS_ENTITIES[0].id, // 기본값: 첫 번째 사업자
    baseSalary: '',
    ssn: '',
    address: '',
    phone: '',
    bankName: '',
    accountNumber: '',
    joinDate: ''
  });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    const result = await getRegularEmployeesMaster();
    if (result.success) {
      setEmployees(result.employees);
    }
    setLoading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const employeeData = {
      ...formData,
      baseSalary: Number(formData.baseSalary),
      isActive: true
    };

    const employeeId = formData.employeeId || `EMP${Date.now()}`;
    const result = await saveRegularEmployeeMaster(employeeId, employeeData);

    if (result.success) {
      alert(editingEmployee ? '직원 정보가 수정되었습니다.' : '직원이 등록되었습니다.');
      setShowAddForm(false);
      setEditingEmployee(null);
      setFormData({
        employeeId: '',
        name: '',
        position: '',
        department: '',
        baseSalary: '',
        ssn: '',
        address: '',
        phone: '',
        bankName: '',
        accountNumber: '',
        joinDate: ''
      });
      fetchEmployees();
    } else {
      alert('저장 실패: ' + result.error);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setFormData({
      employeeId: employee.id,
      name: employee.name || '',
      position: employee.position || '',
      department: employee.department || '',
      baseSalary: employee.baseSalary || '',
      ssn: employee.ssn || '',
      address: employee.address || '',
      phone: employee.phone || '',
      bankName: employee.bankName || '',
      accountNumber: employee.accountNumber || '',
      joinDate: employee.joinDate || ''
    });
    setShowAddForm(true);
  };

  const handleDelete = async (employeeId) => {
    if (window.confirm('정말로 이 직원을 삭제하시겠습니까?')) {
      const result = await deleteRegularEmployeeMaster(employeeId);
      if (result.success) {
        alert('직원이 삭제되었습니다.');
        fetchEmployees();
      } else {
        alert('삭제 실패: ' + result.error);
      }
    }
  };

  const maskSSN = (ssn) => {
    if (!ssn || ssn.length < 13) return ssn;
    return `${ssn.substring(0, 6)}-${ssn.substring(6, 7)}******`;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  return (
    <div className="container mx-auto p-4">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">정규직원 마스터 관리</h2>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            {showAddForm ? '취소' : '직원 추가'}
          </button>
        </div>

        {showAddForm && (
          <div className="mb-6 p-4 border rounded">
            <h3 className="text-lg font-bold mb-4">
              {editingEmployee ? '직원 정보 수정' : '신규 직원 등록'}
            </h3>
            <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">이름 *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">직급</label>
                <input
                  type="text"
                  value={formData.position}
                  onChange={(e) => setFormData({...formData, position: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">부서</label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({...formData, department: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">사업자 *</label>
                <select
                  required
                  value={formData.businessEntity}
                  onChange={(e) => setFormData({...formData, businessEntity: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                >
                  {BUSINESS_ENTITIES.map(entity => (
                    <option key={entity.id} value={entity.id}>
                      {entity.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">기본급 *</label>
                <input
                  type="number"
                  required
                  value={formData.baseSalary}
                  onChange={(e) => setFormData({...formData, baseSalary: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">주민등록번호</label>
                <input
                  type="text"
                  value={formData.ssn}
                  onChange={(e) => setFormData({...formData, ssn: e.target.value})}
                  placeholder="숫자만 입력 (예: 9001011234567)"
                  maxLength="13"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">전화번호</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium mb-1">주소</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">은행명</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({...formData, bankName: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">계좌번호</label>
                <input
                  type="text"
                  value={formData.accountNumber}
                  onChange={(e) => setFormData({...formData, accountNumber: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">입사일</label>
                <input
                  type="date"
                  value={formData.joinDate}
                  onChange={(e) => setFormData({...formData, joinDate: e.target.value})}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>

              <div className="col-span-2 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingEmployee(null);
                    setFormData({
                      employeeId: '',
                      name: '',
                      position: '',
                      department: '',
                      businessEntity: BUSINESS_ENTITIES[0].id,
                      baseSalary: '',
                      ssn: '',
                      address: '',
                      phone: '',
                      bankName: '',
                      accountNumber: '',
                      joinDate: ''
                    });
                  }}
                  className="px-4 py-2 border rounded hover:bg-gray-100"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {editingEmployee ? '수정' : '등록'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? (
          <div className="text-center py-8">
            <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600"></div>
            <p className="mt-2">로딩 중...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full table-auto border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-4 py-2">사번</th>
                  <th className="border px-4 py-2">이름</th>
                  <th className="border px-4 py-2">사업자</th>
                  <th className="border px-4 py-2">부서</th>
                  <th className="border px-4 py-2">직급</th>
                  <th className="border px-4 py-2">기본급</th>
                  <th className="border px-4 py-2">주민등록번호</th>
                  <th className="border px-4 py-2">전화번호</th>
                  <th className="border px-4 py-2">입사일</th>
                  <th className="border px-4 py-2">작업</th>
                </tr>
              </thead>
              <tbody>
                {employees.length > 0 ? (
                  employees.map((employee) => (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="border px-4 py-2">{employee.id}</td>
                      <td className="border px-4 py-2">{employee.name}</td>
                      <td className="border px-4 py-2">
                        {BUSINESS_ENTITIES.find(e => e.id === employee.businessEntity)?.name || employee.businessEntity || '-'}
                      </td>
                      <td className="border px-4 py-2">{employee.department || '-'}</td>
                      <td className="border px-4 py-2">{employee.position || '-'}</td>
                      <td className="border px-4 py-2 text-right">
                        ₩{formatCurrency(employee.baseSalary || 0)}
                      </td>
                      <td className="border px-4 py-2">{maskSSN(employee.ssn) || '-'}</td>
                      <td className="border px-4 py-2">{employee.phone || '-'}</td>
                      <td className="border px-4 py-2">{employee.joinDate || '-'}</td>
                      <td className="border px-4 py-2">
                        <button
                          onClick={() => handleEdit(employee)}
                          className="bg-green-500 text-white px-2 py-1 rounded text-sm mr-1 hover:bg-green-600"
                        >
                          수정
                        </button>
                        <button
                          onClick={() => handleDelete(employee.id)}
                          className="bg-red-500 text-white px-2 py-1 rounded text-sm hover:bg-red-600"
                        >
                          삭제
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="10" className="border px-4 py-8 text-center text-gray-500">
                      등록된 직원이 없습니다.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegularEmployeeManagement;